import { useState, useCallback, useEffect, useMemo, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { useCompanyConfig } from '@/lib/use-company-config';
import { getLayerLabel, getLayerOrder } from '@/lib/investment-layers';
import { scoreAllPrograms, sortByPriority } from '@/lib/scoring';
import DeckShell from '@/components/shared/DeckShell';
import CoverSlide from '@/components/roadmap/CoverSlide';
import IntroductionSlide from '@/components/roadmap/IntroductionSlide';
import RequirementsSlide from '@/components/roadmap/RequirementsSlide';
import SummarySlide from '@/components/roadmap/SummarySlide';
import PriorityProgramsSlide from '@/components/roadmap/PriorityProgramsSlide';
import NotPrioritizingSlide from '@/components/roadmap/NotPrioritizingSlide';
import FundingRealitySlide from '@/components/roadmap/FundingRealitySlide';
import ExecutiveActionSlide from '@/components/roadmap/ExecutiveActionSlide';
import ProgramTableSlide from '@/components/roadmap/ProgramTableSlide';
import CategoryDetailSlide from '@/components/roadmap/CategoryDetailSlide';
import LayerGroupSlide from '@/components/roadmap/LayerGroupSlide';
import TimelineSlide, {
  DeferredPrioritySlide,
  hasDeferredPriorityPrograms,
} from '@/components/roadmap/TimelineSlide';
import DeadlineCalendarSlide from '@/components/roadmap/DeadlineCalendarSlide';
import RoadmapDeliverablesSlide from '@/components/roadmap/RoadmapDeliverablesSlide';
import DecisionMemoSlide from '@/components/roadmap/DecisionMemoSlide';
import { awardAmountMaxToUsd } from '@/lib/realistic-funding';
import { DisplayOptionsContext, type ResolvedDisplayOptions } from '@/lib/display-options';
import type { ViewMode, GoNoGo, GrantProgram, ProgramLayerId } from '@/types/roadmap.types';
import { CompanyConfigPlaceholder } from '@/components/shared/CompanyConfigPlaceholder';
import { useDeckDocumentTitle } from '@/lib/use-deck-document-title';

function groupByLayer(
  programs: GrantProgram[],
  layerOrder: ProgramLayerId[],
): { layer: ProgramLayerId; programs: GrantProgram[] }[] {
  const orderSet = new Set(layerOrder);
  const groups = layerOrder
    .map((layer) => ({
      layer,
      programs: programs.filter((p) => p.investment_layer === layer),
    }))
    .filter((g) => g.programs.length > 0);

  const uncategorized = programs.filter((p) => !orderSet.has(p.investment_layer));
  if (uncategorized.length > 0) {
    groups.push({ layer: 'general', programs: uncategorized });
  }
  return groups;
}

function formatFunding(programs: GrantProgram[]): string {
  const total = programs.reduce((sum, p) => sum + awardAmountMaxToUsd(p), 0);
  if (total >= 1_000_000) return `$${(total / 1_000_000).toFixed(1)}M`;
  if (total >= 1_000) return `$${(total / 1_000).toFixed(0)}K`;
  return `$${total}`;
}

/**
 * Ensure every rendered deck includes the Decision Memo slide, inserted just
 * after `summary` if a saved `deck_recipe` predates it. If neither `summary`
 * nor `decision-memo` is present, prepends it to the start of the deck.
 */
function ensureDecisionMemo(keys: string[]): string[] {
  if (keys.includes('decision-memo')) return keys;
  const idx = keys.indexOf('summary');
  if (idx === -1) return ['decision-memo', ...keys];
  return [...keys.slice(0, idx + 1), 'decision-memo', ...keys.slice(idx + 1)];
}

export interface SlideRegistryEntry {
  label: string;
  component: ReactElement | null;
}

export default function RoadmapPresentation() {
  const { companyId } = useParams<{ companyId: string }>();
  const [mode, setMode] = useState<ViewMode>('presentation');
  const { config, loading, error } = useCompanyConfig(companyId);

  const scoredPrograms = useMemo(() => {
    if (!config) return [];
    return sortByPriority(scoreAllPrograms(config.programs));
  }, [config]);

  const tieredPrograms = useMemo(() => {
    const primaryCount = config?.primary_count ?? 3;
    let assigned = 0;
    return scoredPrograms.map((p) => {
      if (p.go_no_go === 'go' && assigned < primaryCount) {
        assigned++;
        return { ...p, go_no_go: 'primary' as GoNoGo };
      }
      return p;
    });
  }, [scoredPrograms, config?.primary_count]);

  const layerOrder = useMemo(
    () => getLayerOrder(config?.investment_layers),
    [config?.investment_layers],
  );

  const layerGroups = useMemo(
    () => groupByLayer(tieredPrograms, layerOrder),
    [tieredPrograms, layerOrder],
  );

  const [liveOrder, setLiveOrder] = useState<string[] | null>(null);

  const handleReorder = useCallback((newOrder: string[]) => {
    setLiveOrder(newOrder);
  }, []);

  const hiddenStorageKey = companyId ? `roadmap:${companyId}:hiddenSlides` : null;

  const [hiddenSlides, setHiddenSlides] = useState<Set<string>>(() => {
    if (typeof window === 'undefined' || !hiddenStorageKey) return new Set();
    try {
      const raw = window.localStorage.getItem(hiddenStorageKey);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? new Set<string>(parsed.filter((k): k is string => typeof k === 'string')) : new Set();
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !hiddenStorageKey) return;
    try {
      window.localStorage.setItem(hiddenStorageKey, JSON.stringify(Array.from(hiddenSlides)));
    } catch {
      // ignore storage failures (quota, private mode, etc.)
    }
  }, [hiddenSlides, hiddenStorageKey]);

  const handleToggleHidden = useCallback((key: string) => {
    setHiddenSlides((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  useDeckDocumentTitle(config?.company?.name);

  if (loading || error || !config) {
    return (
      <CompanyConfigPlaceholder companyId={companyId} loading={loading} error={error} config={config} />
    );
  }

  const stats = {
    programCount: tieredPrograms.length,
    categoryCount: layerGroups.length,
    goCount: tieredPrograms.filter((p) => p.go_no_go === 'go' || p.go_no_go === 'primary').length,
    jurisdictionCount: config.geography?.eligible_jurisdictions?.length ?? 0,
    totalFunding: formatFunding(tieredPrograms),
  };

  const showDeferredPrioritySlide = hasDeferredPriorityPrograms(tieredPrograms);

  const detailSlideKeys = layerGroups.map(({ layer }) => `detail-${layer}`);

  const slideRegistry: Record<string, SlideRegistryEntry> = {
    'cover': {
      label: 'Cover',
      component: <CoverSlide config={config} mode={mode} />,
    },
    'introduction': {
      label: 'Introduction',
      component: <IntroductionSlide config={config} mode={mode} />,
    },
    'summary': {
      label: 'Decision Layer',
      component: <SummarySlide config={config} mode={mode} stats={stats} />,
    },
    'decision-memo': {
      label: 'Decision Memo',
      component: <DecisionMemoSlide mode={mode} memo={config.decision_memo} />,
    },
    'executive-action': {
      label: 'Action Plan',
      component: config.executive_summary
        ? <ExecutiveActionSlide executiveSummary={config.executive_summary} programs={tieredPrograms} mode={mode} />
        : null,
    },
    'deadline-calendar': {
      label: 'Deadline Calendar',
      component: <DeadlineCalendarSlide programs={tieredPrograms} mode={mode} />,
    },
    'deliverables': {
      label: 'Deliverables & Next Steps',
      component: <RoadmapDeliverablesSlide programs={tieredPrograms} mode={mode} />,
    },
    'funding-reality': {
      label: 'Funding & Cost Match',
      component: config.executive_summary
        ? <FundingRealitySlide executiveSummary={config.executive_summary} mode={mode} />
        : null,
    },
    'priority-programs': {
      label: 'Top Programs',
      component: (
        <PriorityProgramsSlide
          programs={tieredPrograms}
          overrides={config.priority_overrides}
          mode={mode}
        />
      ),
    },
    'not-prioritizing': {
      label: 'Not Prioritizing',
      component: <NotPrioritizingSlide programs={tieredPrograms} mode={mode} />,
    },
    'requirements': {
      label: 'Requirements',
      component: <RequirementsSlide checklist={config.eligibility_checklist} mode={mode} />,
    },
    'eligibility': {
      label: 'Eligibility',
      component: <RequirementsSlide checklist={config.eligibility_checklist} mode={mode} />,
    },
    'program-table': {
      label: 'Program Table',
      component: <ProgramTableSlide programs={tieredPrograms} mode={mode} />,
    },
    'layer-overview': {
      label: 'By Objective',
      component: (
        <LayerGroupSlide
          programs={tieredPrograms}
          mode={mode}
          investmentLayers={config.investment_layers}
          priorityOverrides={config.priority_overrides}
        />
      ),
    },
    ...Object.fromEntries(
      layerGroups.map(({ layer, programs: progs }) => [
        `detail-${layer}`,
        {
          label: getLayerLabel(layer, config.investment_layers),
          component: (
            <CategoryDetailSlide
              category={getLayerLabel(layer, config.investment_layers)}
              programs={progs}
              mode={mode}
            />
          ),
        },
      ]),
    ),
    'timeline': {
      label: 'Action Priority',
      component: <TimelineSlide programs={tieredPrograms} mode={mode} />,
    },
    'deferred-priority': {
      label: 'Prepare · Monitor · Pass',
      component: showDeferredPrioritySlide
        ? <DeferredPrioritySlide programs={tieredPrograms} mode={mode} />
        : null,
    },
  };

  const DEFAULT_SLIDE_ORDER: string[] = [
    'cover',
    'introduction',
    'summary',
    'decision-memo',
    'executive-action',
    'deadline-calendar',
    'funding-reality',
    'priority-programs',
    'not-prioritizing',
    'requirements',
    'program-table',
    'layer-overview',
    ...detailSlideKeys,
    'timeline',
    'deferred-priority',
  ];

  const rawOrder = config.deck_recipe?.slides ?? DEFAULT_SLIDE_ORDER;
  const configOrder = ensureDecisionMemo(rawOrder);
  const order = liveOrder ?? configOrder;

  const displayOptions: ResolvedDisplayOptions = {
    showObjective:
      config.display?.show_objective ??
      tieredPrograms.some((p) => p.investment_layer && p.investment_layer !== 'general'),
    showEntryPath:
      config.display?.show_entry_path ?? tieredPrograms.some((p) => Boolean(p.entry_path)),
  };

  const slides = order
    .map((key) => {
      if (hiddenSlides.has(key)) return null;
      const def = slideRegistry[key];
      if (!def?.component) return null;
      return { key, label: def.label, component: def.component };
    })
    .filter((s): s is { key: string; label: string; component: ReactElement } => s !== null);

  const slideLabels = Object.fromEntries(Object.entries(slideRegistry).map(([k, v]) => [k, v.label]));

  const disabledSlides = new Set(Object.entries(slideRegistry).filter(([, v]) => !v.component).map(([k]) => k));

  return (
    <DisplayOptionsContext.Provider value={displayOptions}>
      <DeckShell
        slides={slides}
        title="Grant Roadmap"
        subtitle={config.company.name}
        altLink={{
          to: `/${config.id}/proposal`,
          label: '← View Proposal',
        }}
        mode={mode}
        onModeChange={setMode}
        clientBrand={config.clientBrand}
        whiteLabel={config.whiteLabel}
        slideOrder={order}
        slideLabels={slideLabels}
        disabledSlides={disabledSlides}
        hiddenSlides={hiddenSlides}
        onToggleHidden={handleToggleHidden}
        onSlideReorder={handleReorder}
        companyId={config.id}
      />
    </DisplayOptionsContext.Provider>
  );
}
